import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const TudorADUDesignSanCarlos = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Tudor ADU Design & Construction in San Carlos",
    "description": "Create timeless Tudor ADUs in San Carlos featuring decorative half-timbering, steep-pitched roofs, and Old World charm.",
    "url": "https://www.udaadu.com/tudor-adu-design-san-carlos",
    "mainEntity": {
      "@type": "Service",
      "name": "Tudor ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "San Carlos, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/tudor-adu-design-san-carlos" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Tudor ADU Design & Construction in San Carlos
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create timeless Tudor ADUs in San Carlos featuring decorative half-timbering, steep-pitched roofs, and Old World charm.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Tudor ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Why Choose Tudor ADUs in San Carlos?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Distinguished Character</h3>
              <p className="text-muted-foreground">
                Tudor architecture brings sophisticated Old World charm to San Carlos, creating ADUs with distinctive character and timeless appeal.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Architectural Drama</h3>
              <p className="text-muted-foreground">
                Steep-pitched roofs, decorative half-timbering, and leaded glass windows create visually striking ADUs that enhance property value.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Cozy Interiors</h3>
              <p className="text-muted-foreground">
                Exposed beams, built-in features, and intimate spaces create warm, inviting living environments perfect for guests or rental income.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Tudor ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Decorative half-timbering and stucco walls</li>
                <li>• Steep-pitched gable roofs with clay tiles</li>
                <li>• Arched doorways and mullioned windows</li>
                <li>• Stone or brick accent details</li>
                <li>• Chimney features and decorative brackets</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Exposed wooden beams and vaulted ceilings</li>
                <li>• Leaded glass windows and built-in cabinetry</li>
                <li>• Hardwood floors and stone fireplaces</li>
                <li>• Rich wood paneling and traditional millwork</li>
                <li>• Cozy reading nooks and window seats</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">San Carlos Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Neighborhood Integration</h3>
              <p className="text-muted-foreground">
                Tudor ADUs complement San Carlos's diverse architectural styles while adding unique character that respects the community's aesthetic values.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Quality Construction</h3>
              <p className="text-muted-foreground">
                Authentic Tudor details require skilled craftsmanship and premium materials to achieve the intricate woodwork and stonework that define the style.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-foreground">$285,000 - $460,000</p>
            <p className="text-muted-foreground">
              Tudor ADUs require premium materials and specialized craftsmanship. Custom millwork and authentic details enhance value and appeal.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-foreground">Start Your Tudor ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to add Old World elegance to your San Carlos property? Let's create your perfect Tudor-style ADU.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-foreground">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/victorian-adu-design-san-carlos">Victorian ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/craftsman-adu-design-san-carlos">Craftsman ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/english-cottage-adu-design-san-carlos">English Cottage ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default TudorADUDesignSanCarlos;